import { ChangeDetectionStrategy, Component, computed, effect, input, output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import {
  AgentZonesUpdateDto,
  Country,
  FRENCH_DEPARTMENTS_MAP,
  FRENCH_REGIONS,
  FrenchDepartment,
  FrenchRegion,
  InterventionZoneDto,
} from '@workspace/data-types';

@Component({
  selector: 'etxe-agent-zones-form',
  imports: [MatFormFieldModule, MatSelectModule, MatInputModule, MatIconModule, MatButtonModule],
  templateUrl: './agent-zones-form.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentZonesFormComponent {
  readonly primaryRegion = input<FrenchRegion | undefined>(FrenchRegion.OCCITANIE);
  readonly interventionZones = input<InterventionZoneDto[]>([]);

  readonly primaryRegionChange = output<FrenchRegion>();
  readonly interventionZonesChange = output<InterventionZoneDto[]>();
  readonly validityChanged = output<boolean>();
  readonly dtoChanged = output<{ dto: AgentZonesUpdateDto; changed: boolean }>();

  readonly availableRegions = FRENCH_REGIONS;
  readonly deptSearchQuery = signal<string>('');

  readonly internalPrimaryRegion = signal<FrenchRegion | undefined>(undefined);
  readonly internalInterventionZones = signal<InterventionZoneDto[]>([]);

  constructor() {
    effect(() => {
      const reg = this.primaryRegion();
      const zones = this.interventionZones();
      this.internalPrimaryRegion.set(reg);
      this.internalInterventionZones.set(zones ? [...zones] : []);
      this.validityChanged.emit(true);
      this.dtoChanged.emit({
        dto: {
          primaryRegion: reg,
          interventionZones: zones || [],
        },
        changed: false,
      });
    });
  }

  readonly selectedDepartments = computed(() => {
    const zones = this.internalInterventionZones();
    const frZone = zones.find((z) => z.country === Country.FRANCE);
    return frZone?.departments || [];
  });

  readonly regionSelectionCounts = computed(() => {
    const counts: Record<string, number> = {};
    const selected = new Set(this.selectedDepartments());

    for (const region of this.availableRegions) {
      let count = 0;
      for (const dept of region.departments) {
        if (selected.has(dept)) count++;
      }
      counts[region.code] = count;
    }
    return counts;
  });

  readonly currentRegionDepartments = computed(() => {
    const regCode = this.internalPrimaryRegion();
    if (!regCode) return [];
    const region = this.availableRegions.find((r) => r.code === regCode);
    if (!region) return [];

    return region.departments.map((dept) => ({
      dept,
      info: FRENCH_DEPARTMENTS_MAP[dept],
    }));
  });

  readonly deptSearchResults = computed(() => {
    const query = this.deptSearchQuery().trim().toLowerCase();
    if (!query) return [];

    const normalizedQuery = query.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const results: {
      dept: FrenchDepartment;
      code: string;
      name: string;
      regionName: string;
      isSelected: boolean;
    }[] = [];

    const selectedSet = new Set(this.selectedDepartments());

    for (const [deptKey, info] of Object.entries(FRENCH_DEPARTMENTS_MAP)) {
      const dept = deptKey as FrenchDepartment;
      const normalizedName = info.name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
      const codeMatch = info.code.startsWith(query);
      const nameMatch = normalizedName.includes(normalizedQuery);

      if (codeMatch || nameMatch) {
        const regionInfo = this.availableRegions.find((r) => r.code === info.region);
        results.push({
          dept,
          code: info.code,
          name: info.name,
          regionName: regionInfo?.name || '',
          isSelected: selectedSet.has(dept),
        });
      }
    }

    return results.slice(0, 8);
  });

  setPrimaryRegion(reg: FrenchRegion): void {
    this.internalPrimaryRegion.set(reg);
    this.primaryRegionChange.emit(reg);
    this.emitState();
  }

  toggleDepartment(dept: FrenchDepartment): void {
    const current = this.selectedDepartments();
    const updated = current.includes(dept) ? current.filter((d) => d !== dept) : [...current, dept];
    this.updateFranceDepartments(updated);
  }

  selectAllRegionDepartments(): void {
    const regionDepts = this.currentRegionDepartments().map((d) => d.dept);
    const current = new Set(this.selectedDepartments());
    regionDepts.forEach((d) => current.add(d));
    this.updateFranceDepartments(Array.from(current));
  }

  deselectAllRegionDepartments(): void {
    const regionDepts = new Set(this.currentRegionDepartments().map((d) => d.dept));
    const updated = this.selectedDepartments().filter((d) => !regionDepts.has(d));
    this.updateFranceDepartments(updated);
  }

  removeDepartment(dept: FrenchDepartment): void {
    const updated = this.selectedDepartments().filter((d) => d !== dept);
    this.updateFranceDepartments(updated);
  }

  getDeptInfo(dept: FrenchDepartment) {
    const info = FRENCH_DEPARTMENTS_MAP[dept];
    const regionInfo = info ? this.availableRegions.find((r) => r.code === info.region) : null;
    return {
      code: info?.code || dept,
      name: info?.name || dept,
      regionName: regionInfo?.name || '',
    };
  }

  private updateFranceDepartments(departments: FrenchDepartment[]): void {
    const zones = [...this.internalInterventionZones()];
    const frIndex = zones.findIndex((z) => z.country === Country.FRANCE);

    if (frIndex >= 0) {
      zones[frIndex] = {
        ...zones[frIndex],
        departments,
      };
    } else {
      zones.push({
        country: Country.FRANCE,
        departments,
      });
    }

    this.internalInterventionZones.set(zones);
    this.interventionZonesChange.emit(zones);
    this.emitState();
  }

  private emitState(): void {
    this.validityChanged.emit(true);
    this.dtoChanged.emit({
      dto: {
        primaryRegion: this.internalPrimaryRegion(),
        interventionZones: this.internalInterventionZones(),
      },
      changed: true,
    });
  }
}
